import type { Member, StudioPresence } from '@hive/shared';
import { HardDrive, LogOut, Menu, RefreshCw, Settings } from 'lucide-react';
import { useState } from 'react';
import { MemberAvatar } from '@/components/MemberAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PresenceStatus } from '@/presence/useStudioPresence';
import { AccountSettings } from './AccountSettings';
import { OnlineList } from './OnlineList';

export function TopBar({
  onMenu,
  member,
  online,
  presenceStatus,
  onSignOut,
  onConnectDrive,
  updateReady = false,
}: {
  /** Opens the sidebar as a menu (phones; the button only shows on narrow screens). */
  onMenu?: () => void;
  member: Member;
  online: StudioPresence[];
  presenceStatus: PresenceStatus;
  onSignOut: () => void;
  /** Absent until Vault (Phase 3) is in. */
  onConnectDrive?: () => void;
  /** A newer build is live: offer a reload. */
  updateReady?: boolean;
}) {
  const [settings, setSettings] = useState(false);
  return (
    <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-[linear-gradient(180deg,rgb(12_14_22/0.9),rgb(7_8_13/0.6))] px-5 max-[860px]:gap-3 max-[860px]:px-3">
      {onMenu ? (
        <button
          type="button"
          aria-label="Open menu"
          className="-ml-1 cursor-pointer p-1 text-cyan min-[861px]:hidden"
          onClick={onMenu}
        >
          <Menu className="size-5" aria-hidden />
        </button>
      ) : null}
      <p className="font-display text-sm font-bold tracking-[0.18em] text-text uppercase">The Hive</p>
      <span className="hud-label hidden md:inline">Studio</span>
      <div className="ml-auto flex items-center gap-4">
        {updateReady ? (
          <button
            type="button"
            onClick={() => window.location.reload()}
            aria-label="Update ready, reload"
            title="A newer version of Hive is live. Reload to use it."
            className="flex cursor-pointer items-center gap-1.5 border border-yellow px-2 py-1 font-mono text-[10px] tracking-[0.12em] text-yellow uppercase hover:bg-yellow hover:text-ink"
          >
            <RefreshCw className="size-3" aria-hidden />
            <span>
              Update<span className="max-[860px]:hidden"> ready · reload</span>
            </span>
          </button>
        ) : null}
        <OnlineList online={online} status={presenceStatus} selfId={member.id} />
        <DropdownMenu>
          <DropdownMenuTrigger
            className="cursor-pointer rounded-none outline-none focus-visible:outline-2 focus-visible:outline-yellow"
            aria-label={`Account menu for ${member.display_name}`}
          >
            <MemberAvatar name={member.display_name} avatarUrl={member.avatar_url} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <span className="block font-semibold">{member.display_name}</span>
              {member.nickname ? (
                <span className="block font-mono text-[11px] text-cyan">@{member.nickname}</span>
              ) : null}
              <span className="block font-mono text-[11px] text-muted">{member.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setSettings(true)}>
              <Settings aria-hidden /> Account settings
            </DropdownMenuItem>
            {onConnectDrive ? (
              <DropdownMenuItem onSelect={onConnectDrive}>
                <HardDrive aria-hidden /> Connect Google Drive
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={onSignOut}>
              <LogOut aria-hidden /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {settings ? <AccountSettings member={member} onClose={() => setSettings(false)} /> : null}
    </header>
  );
}
